var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { constructArgumentCollection, deconstructArgumentCollection } from './arguments';
import { DominoBuilderError, DominoBuilderErrorScope, DominoBuilderErrorSeverity, DominoBuilderErrorType, filterErrors, } from './errors';
import { getIsNamedTriggerCall, getIsTriggerGenericTyped } from './helpers';
import { toParameterDefinition } from './parameters';
import { constructContextSymbol } from './symbols';
import { HandleType } from './types';
export function constructTriggerCall(triggerCall, resourceRegistry, symbolTable) {
    var _a, _b, _c, _d, _e, _f, _g;
    return __awaiter(this, void 0, void 0, function* () {
        const errors = [];
        if (!!!triggerCall) {
            errors.push(new DominoBuilderError(DominoBuilderErrorType.MissingPropertyWarning, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, 'Missing a trigger call for the flow', {
                path: 'Trigger',
            }));
            return {
                errors,
                getErrorAggregate: (scope, severity) => {
                    return filterErrors(scope, severity, errors);
                },
            };
        }
        const isNamedTrigger = getIsNamedTriggerCall(triggerCall);
        const triggerName = isNamedTrigger ? triggerCall.name : triggerCall === null || triggerCall === void 0 ? void 0 : triggerCall.toString();
        let triggerDefinition;
        try {
            triggerDefinition = yield resourceRegistry.getResource(triggerName);
        }
        catch (error) {
            errors.push(error);
        }
        // Construct argument collection used for design-time trigger configuration
        const argsCollection = yield constructArgumentCollection(resourceRegistry, (_a = triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.params) !== null && _a !== void 0 ? _a : [], isNamedTrigger ? (_b = triggerCall === null || triggerCall === void 0 ? void 0 : triggerCall.args) !== null && _b !== void 0 ? _b : [] : [], HandleType.TriggerArgument);
        let effectiveScope = symbolTable.calculateEffectiveScope('global');
        argsCollection.args.forEach(arg => { var _a; return (_a = arg.value) === null || _a === void 0 ? void 0 : _a.construct(effectiveScope, -1); });
        // Construct argument collection for design-time configuration of generic types
        const genericTypeArgsCollection = yield constructArgumentCollection(resourceRegistry, 
        /**
         * The `type` string here serves no purpose for now (it can be anything without affecting builder behavior as it is set at the moment). But documenting that every provided genericTypeArg
         * value must be an array of Parameter at the moment.
         */
        (_d = (_c = triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.genericTypeParams) === null || _c === void 0 ? void 0 : _c.map(toParameterDefinition)) !== null && _d !== void 0 ? _d : [], isNamedTrigger && getIsTriggerGenericTyped(triggerCall)
            ? (_e = triggerCall === null || triggerCall === void 0 ? void 0 : triggerCall.genericTypeArgs) !== null && _e !== void 0 ? _e : []
            : [], HandleType.TriggerGenericTypeArgument);
        effectiveScope = symbolTable.calculateEffectiveScope('global');
        // Update entity registry with constructed anonymous types
        genericTypeArgsCollection.args.forEach(arg => {
            // validate these arguments to build out the expressions, same as regular args
            arg.value.forEach(item => {
                item === null || item === void 0 ? void 0 : item.construct(effectiveScope, -1);
            });
            resourceRegistry.setResourceToCache(arg.name, {
                name: arg.name,
                properties: arg.value.map((genericArg) => genericArg.members[0].value),
                spec: {},
            });
        });
        const context = yield constructArgumentCollection(resourceRegistry, (_f = triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.contextType) !== null && _f !== void 0 ? _f : [], {}, HandleType.TriggerContextArgument, undefined, true, (_g = triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.genericTypeParams) !== null && _g !== void 0 ? _g : []);
        yield constructContextSymbol(context.args, resourceRegistry, symbolTable); // save expected trigger context to symbol table
        return {
            name: triggerName,
            label: triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.label,
            description: triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.description,
            alerts: triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.alerts,
            icon: triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.icon,
            argsCollection,
            genericTypeArgsCollection: genericTypeArgsCollection,
            category: triggerDefinition === null || triggerDefinition === void 0 ? void 0 : triggerDefinition.category,
            context: context,
            errors,
            getErrorAggregate: (scope, severity) => {
                var _a, _b, _c;
                return filterErrors(scope, severity, errors)
                    .concat((_a = argsCollection === null || argsCollection === void 0 ? void 0 : argsCollection.getErrorAggregate(scope, severity)) !== null && _a !== void 0 ? _a : [])
                    .concat((_b = genericTypeArgsCollection === null || genericTypeArgsCollection === void 0 ? void 0 : genericTypeArgsCollection.getErrorAggregate(scope, severity)) !== null && _b !== void 0 ? _b : [])
                    .concat((_c = context === null || context === void 0 ? void 0 : context.getErrorAggregate(scope, severity)) !== null && _c !== void 0 ? _c : []);
            },
        };
    });
}
export function deconstructTriggerCall(trigger) {
    var _a, _b;
    const deconstructedTrigger = { name: trigger === null || trigger === void 0 ? void 0 : trigger.name };
    if (((_a = trigger === null || trigger === void 0 ? void 0 : trigger.argsCollection) === null || _a === void 0 ? void 0 : _a.args.length) > 0) {
        // Trigger argument collections must always be written back as objects - arrays are invalid since they break hash generation
        trigger.argsCollection.originalFormat = 'object';
        //need types ^2.3.21 on core
        deconstructedTrigger.args = deconstructArgumentCollection(trigger === null || trigger === void 0 ? void 0 : trigger.argsCollection, []);
    }
    if (((_b = trigger === null || trigger === void 0 ? void 0 : trigger.genericTypeArgsCollection) === null || _b === void 0 ? void 0 : _b.args.length) > 0) {
        trigger.genericTypeArgsCollection.originalFormat = 'object';
        deconstructedTrigger.genericTypeArgs = deconstructArgumentCollection(trigger.genericTypeArgsCollection, []);
    }
    return deconstructedTrigger;
}
//# sourceMappingURL=triggers.js.map